import { toComponentName } from "@lhd/utility";
import { Button, Dropdown, MenuProps, Modal, Space, notification } from "antd";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FcSettings } from "react-icons/fc";
import { FormActionContext } from "../..";
import {
  APP_PART_TYPES,
  COLUMN_WIDTH,
  ITEMS_MENU,
  KEY_MENU,
  LEVEL_LINE,
  WARNING_DELETE_MESSAGE,
} from "../../constants";
import { AppPartType } from "../../types";
import ModalSetting from "../Modal/ModalSetting";
import * as AppPartComponents from "./AppPartComponents";
import { Styles } from "./styles";
import {
  generateUuid,
  isEmployeeInfoChildAppPartType,
} from "@lhd/features/Apps/utils";
import { useGetListDataExist } from "../../hook";
import WarningIcon from "./AppPartComponents/WarningIcon";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import { CopyTypeAppStore } from "@lhd/constants";
import SafeHtml from "@lhd/components/SafeHtml";

type IProps = {
  appPart: AppPartType;
  containerRef: React.RefObject<HTMLDivElement>;
  isDragging?: boolean;
  appCopyType?: keyof typeof CopyTypeAppStore;
};

const AppPart = React.forwardRef<AppPartType, IProps>(
  ({ appPart, containerRef, isDragging, appCopyType }, selectedAppPartRef) => {
    const [editingAppPart, setEditingAppart] = useState<AppPartType>();

    const { onHandleDeleteItem, onHandleDuplicate, setContextState } =
      useContext(FormActionContext);
    const [isHovered, setIsHovered] = useState(false);
    const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
    const [selectedAppPart, setSelectedAppPart] = useState<AppPartType>();
    const { dataExistList } = useGetListDataExist();
    const [isShouldShowAttention, setIsShouldShowAttention] = useState(false);
    const refTimeOut = useRef<NodeJS.Timer>();

    const handleMenuClick: MenuProps["onClick"] = useCallback(
      (e) => {
        switch (e.key) {
          case KEY_MENU.EDIT:
            setContextState((draft) => {
              draft.isDisableDragging = true;
            });
            setEditingAppart(appPart);
            break;
          case KEY_MENU.DUPLICATE:
            onHandleDuplicate({
              originalItem: {
                ...appPart,
                uuid: generateUuid(),
                current_detail_permissions: {
                  specified_app_part_edit: true,
                  specified_app_part_read: true,
                },
              },
              parentI: appPart.item_config_data.parentI || "",
              containerRef,
            });
            break;
          case KEY_MENU.DELETE: {
            if (appCopyType === CopyTypeAppStore.syncConfigFromOriginal) {
              notification.error({
                message: "こちらは改変不可・自動更新Bizシートです",
                duration: 3,
              });
            } else {
              if (dataExistList.includes(appPart.id?.toString() || "")) {
                setIsOpenConfirmModal(true);
                setSelectedAppPart(appPart);
              } else {
                onHandleDeleteItem({
                  i: appPart.item_config_data.i,
                  parentI: appPart.item_config_data.parentI,
                });
              }
            }

            break;
          }
          case LEVEL_LINE.level_horizontal_0:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );

              if (itemEditIndex > -1) {
                draft.layout[
                  itemEditIndex
                ].item_config_data.level_horizontal = 0;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_horizontal = 0;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_horizontal_1:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );
              if (itemEditIndex > -1) {
                draft.layout[
                  itemEditIndex
                ].item_config_data.level_horizontal = 1;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_horizontal = 1;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_horizontal_2:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );
              if (itemEditIndex > -1) {
                draft.layout[
                  itemEditIndex
                ].item_config_data.level_horizontal = 2;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_horizontal = 2;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_horizontal_3:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );
              if (itemEditIndex > -1) {
                draft.layout[
                  itemEditIndex
                ].item_config_data.level_horizontal = 3;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_horizontal = 3;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_horizontal_4:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );
              if (itemEditIndex > -1) {
                draft.layout[
                  itemEditIndex
                ].item_config_data.level_horizontal = 4;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_horizontal = 4;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_vertical_0:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );

              if (itemEditIndex > -1) {
                draft.layout[itemEditIndex].item_config_data.level_vertical = 0;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_vertical = 0;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_vertical_1:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );

              if (itemEditIndex > -1) {
                draft.layout[itemEditIndex].item_config_data.level_vertical = 1;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_vertical = 1;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_vertical_2:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );

              if (itemEditIndex > -1) {
                draft.layout[itemEditIndex].item_config_data.level_vertical = 2;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_vertical = 2;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_vertical_3:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );

              if (itemEditIndex > -1) {
                draft.layout[itemEditIndex].item_config_data.level_vertical = 3;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_vertical = 3;
                        break;
                      }
                    }
                  }
                }
              }
            });
            break;
          case LEVEL_LINE.level_vertical_4:
            setContextState((draft) => {
              const itemEditIndex = draft.layout.findIndex(
                (x) =>
                  (x.id != null && appPart.id != null && x.id === appPart.id) ||
                  (x.uuid != null &&
                    appPart.uuid != null &&
                    x.uuid === appPart.uuid),
              );

              if (itemEditIndex > -1) {
                draft.layout[itemEditIndex].item_config_data.level_vertical = 4;
              } else {
                for (let i = 0; i < draft.layout.length; i++) {
                  if (draft.layout[i].children) {
                    const children = draft.layout[i].children;
                    if (children) {
                      const childIndex = children.findIndex(
                        (child) =>
                          (child?.id != null &&
                            appPart?.id != null &&
                            child?.id === appPart?.id) ||
                          (child.uuid != null &&
                            appPart?.uuid != null &&
                            child?.uuid === appPart?.uuid),
                      );

                      if (childIndex > -1) {
                        children[
                          childIndex
                        ].item_config_data.level_vertical = 4;
                        break;
                      }
                    }
                  }
                }
              }
            });
        }
      },
      [
        appPart,
        onHandleDuplicate,
        setContextState,
        onHandleDeleteItem,
        dataExistList,
      ],
    );

    const deleteAppPart = () => {
      setIsOpenConfirmModal(false);
      if (!selectedAppPart) return;
      onHandleDeleteItem({
        i: selectedAppPart.item_config_data.i,
        parentI: selectedAppPart.item_config_data.parentI,
      });
    };
    const componentName = useMemo(() => {
      return `${toComponentName(appPart.app_part_type)}`;
    }, [appPart]) as keyof typeof AppPartComponents;

    const AppPartComponent = isEmployeeInfoChildAppPartType(
      appPart.app_part_type,
    )
      ? AppPartComponents.EmployeeInfoChildSelection
      : AppPartComponents[componentName] || AppPartComponents.AutoFilled;

    useEffect(() => {
      if (containerRef.current?.parentElement && appPart.is_attention) {
        let idTimeOut: NodeJS.Timeout | undefined;
        containerRef.current.parentElement.onscroll = () => {
          clearTimeout(idTimeOut);
          idTimeOut = setTimeout(() => {
            setIsShouldShowAttention(true);
          }, 100);
        };
      }
    }, []);

    let isNotAllowDrop = false;
    if (appPart.app_part_type === APP_PART_TYPES.TABLE) {
      const appPartType = (selectedAppPartRef as any).current?.app_part_type;
      isNotAllowDrop =
        [
          APP_PART_TYPES.TABLE,
          APP_PART_TYPES.LABEL,
          APP_PART_TYPES.FIELD_GROUP,
          APP_PART_TYPES.VERTICAL_BORDER,
          APP_PART_TYPES.HORIZONTAL_BORDER,
        ].includes(appPartType) && !!isDragging;
    } else if (appPart.app_part_type === APP_PART_TYPES.FIELD_GROUP) {
      const appPartType = (selectedAppPartRef as any).current?.app_part_type;
      isNotAllowDrop =
        [APP_PART_TYPES.TABLE, APP_PART_TYPES.FIELD_GROUP].includes(
          appPartType,
        ) && !!isDragging;
    }
    return (
      <Styles className={`app-part-type ${appPart.app_part_type}`}>
        <Modal
          open={isOpenConfirmModal}
          title=""
          onCancel={() => setIsOpenConfirmModal(false)}
          width={600}
          className="warning-delete"
          closable={false}
          footer={[
            <Button key="ok" type="primary" onClick={deleteAppPart}>
              はい
            </Button>,
            <Button key="cancel" onClick={() => setIsOpenConfirmModal(false)}>
              いいえ
            </Button>,
          ]}
        >
          <WarningIcon />
          {WARNING_DELETE_MESSAGE}
        </Modal>
        <ModalSetting
          appPart={editingAppPart}
          setEditingAppPart={setEditingAppart}
        />

        <div
          onMouseEnter={() => {
            clearTimeout(refTimeOut.current);
            setIsHovered(true);
          }}
          onMouseLeave={() =>
            (refTimeOut.current = setTimeout(() => {
              setIsHovered(false);
            }, 220))
          }
          id={`itemContainer_${appPart.item_config_data.i}`}
          className={"item-container ".concat(
            appPart.app_part_type,
            isShouldShowAttention ? " blinking-attention" : "",
            isNotAllowDrop ? " not_allow_drop" : "",
          )}
          style={{
            padding: 0,
          }}
        >
          {isHovered ? (
            <div id="actionButton" className="action-button">
              <Dropdown
                key={appPart.id || appPart.uuid}
                menu={{
                  items:
                    appPart.app_part_type ===
                      APP_PART_TYPES.HORIZONTAL_BORDER ||
                    appPart.app_part_type === APP_PART_TYPES.VERTICAL_BORDER
                      ? ([
                          ...(appPart.app_part_type ===
                          APP_PART_TYPES.HORIZONTAL_BORDER
                            ? [
                                {
                                  key: LEVEL_LINE.level_horizontal_0,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      線
                                      <div
                                        style={{
                                          width: 30,
                                          height: 1,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                                {
                                  key: LEVEL_LINE.level_horizontal_1,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      細
                                      <div
                                        style={{
                                          width: 30,
                                          height: COLUMN_WIDTH * 1,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                                {
                                  key: LEVEL_LINE.level_horizontal_2,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      太
                                      <div
                                        style={{
                                          width: 30,
                                          height: COLUMN_WIDTH * 2,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                                {
                                  key: LEVEL_LINE.level_horizontal_3,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      極
                                      <div
                                        style={{
                                          width: 30,
                                          height: COLUMN_WIDTH * 3,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                                {
                                  key: LEVEL_LINE.level_horizontal_4,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      幅
                                      <div
                                        style={{
                                          width: 30,
                                          height: COLUMN_WIDTH * 4,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                              ].reverse()
                            : [
                                {
                                  key: LEVEL_LINE.level_vertical_0,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      線
                                      <div
                                        style={{
                                          width: 1,
                                          height: 30,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                                {
                                  key: LEVEL_LINE.level_vertical_1,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      細
                                      <div
                                        style={{
                                          width: COLUMN_WIDTH * 1,
                                          height: 30,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                                {
                                  key: LEVEL_LINE.level_vertical_2,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      太
                                      <div
                                        style={{
                                          height: 30,
                                          width: COLUMN_WIDTH * 2,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                                {
                                  key: LEVEL_LINE.level_vertical_3,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      極
                                      <div
                                        style={{
                                          height: 30,
                                          width: COLUMN_WIDTH * 3,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                                {
                                  key: LEVEL_LINE.level_vertical_4,
                                  label: (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 5,
                                        alignItems: "center",
                                      }}
                                    >
                                      幅
                                      <div
                                        style={{
                                          height: 30,
                                          width: COLUMN_WIDTH * 4,
                                          backgroundColor: "black",
                                        }}
                                      ></div>
                                    </div>
                                  ),
                                },
                              ].reverse()),
                          ITEMS_MENU?.[1],
                          ITEMS_MENU?.[2],
                        ] as ItemType[])
                      : ([
                          ITEMS_MENU?.[0],
                          ITEMS_MENU?.[1],
                          ITEMS_MENU?.[2],
                        ] as ItemType[]),
                  onClick: handleMenuClick,
                }}
                placement="bottomRight"
              >
                <div
                  style={{
                    position: "absolute",
                    top: 2,
                    right: -3,
                  }}
                >
                  <FcSettings className="setting-button" />
                </div>
              </Dropdown>
            </div>
          ) : null}
          {![
            APP_PART_TYPES.HORIZONTAL_BORDER,
            APP_PART_TYPES.VERTICAL_BORDER,
            APP_PART_TYPES.BLANK_SPACE,
          ].includes(appPart.app_part_type as any) && (
            <div
              className={`${appPart.app_part_type} ${
                appPart.is_required_field ? "has-required-mark" : ""
              } item_part_type_container`}
            >
              <Space>
                <>
                  {appPart.app_part_type !== "label" ? (
                    <div>
                      {appPart.is_hide_name ? "" : String(appPart.name)}
                    </div>
                  ) : (
                    <SafeHtml
                      html={appPart.is_hide_name ? "" : String(appPart.name)}
                    />
                  )}
                </>
                {appPart.is_required_field && (
                  <div className="required-mark" style={{ height: "22px" }}>
                    *
                  </div>
                )}
              </Space>
            </div>
          )}
          <div
            className={`${
              ![
                APP_PART_TYPES.HORIZONTAL_BORDER,
                APP_PART_TYPES.VERTICAL_BORDER,
                APP_PART_TYPES.BLANK_SPACE,
              ].includes(appPart.app_part_type as any)
                ? "item-box"
                : ""
              // } ${
              //   appPart.item_config_data.layout === "horizontal"
              //     ? "fit-content"
              //     : ""
            }`}
          >
            {AppPartComponent && (
              <AppPartComponent
                appPart={appPart}
                isNotAllowDrop={isNotAllowDrop}
                ref={
                  ["field_group", "table"].includes(appPart.app_part_type)
                    ? selectedAppPartRef
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </Styles>
    );
  },
);
AppPart.displayName = "AppPart";

export default AppPart;
